'use client';

import { useCandidateNavigationOptional, type CandidateSearchFilters } from '@components/contexts/CandidateNavigationContext';
import { PromptBarShell } from './PromptBarShell';
import { PromptBarActionsPopup } from './PromptBarActionsPopup';
import { PromptBarChips } from './PromptBarChips';
import { SearchBranch } from './SearchBranch';
import { ChatBranch } from './ChatBranch';
import { MissionChipsRow } from './MissionChipsRow';
import { usePromptBarMode, type PromptBarMode } from './usePromptBarMode';

interface CandidatePromptBarProps {
  locale: Locale;
  isAuthenticated?: boolean;
  /** Force a mode. Otherwise derived from currentView via usePromptBarMode. */
  modeOverride?: PromptBarMode;
  /** Chat submit handler. Required for chat mode to function; if absent the
   *  branch still renders but the user cannot send a message. */
  onChatSubmit?: (text: string) => Promise<void>;
  /** Search submit handler. Defaults to navigateToCandidateSearch. */
  onSearchSubmit?: (filters: CandidateSearchFilters) => void;
  /** Render in document flow instead of fixed-position overlay. */
  inline?: boolean;
  /** Render just the mission chips instead of the full prompt bar. Used on
   *  surfaces where the input is not useful (company pages). */
  collapsedFab?: boolean;
}

const noopChatSubmit: (text: string) => Promise<void> = async () => {};

export const CandidatePromptBar = ({
  locale,
  isAuthenticated = false,
  modeOverride,
  onChatSubmit,
  onSearchSubmit,
  inline = false,
  collapsedFab = false,
}: CandidatePromptBarProps) => {
  const mode = usePromptBarMode(modeOverride);
  const candidateNav = useCandidateNavigationOptional();
  const handleSearchSubmit = onSearchSubmit ?? candidateNav?.navigateToCandidateSearch;

  // Company-page surfaces (collapsedFab): just the mission chips — no full prompt bar.
  if (collapsedFab) {
    return <PromptBarChips locale={locale} mode={mode} isAuthenticated={isAuthenticated} inline={inline} />;
  }

  const popup = <PromptBarActionsPopup locale={locale} mode={mode} isAuthenticated={isAuthenticated} />;

  return (
    <div className="w-full">
      <PromptBarShell
        inline={inline}
        belowSlot={<MissionChipsRow locale={locale} mode={mode} isAuthenticated={isAuthenticated} className="mt-2" />}
      >
        {mode === 'search' && handleSearchSubmit
          ? <SearchBranch locale={locale} onSubmit={handleSearchSubmit} leftSlot={popup} />
          : <ChatBranch onSubmit={onChatSubmit ?? noopChatSubmit} leftSlot={popup} />}
      </PromptBarShell>
    </div>
  );
};
